import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, CheckCircle, GraduationCap, Loader2, ExternalLink, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const PRIVACY_POLICY_VERSION = "1.0";
const CONSENT_NOTICE_VERSION = "1.0";

export default function ParentConsentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consenting, setConsenting] = useState(null);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'parent') return;
    api.get('/parent/children-consent')
      .then(r => setChildren(r.data))
      .catch(() => toast.error('Failed to load children'))
      .finally(() => setLoading(false));
  }, [user]);

  const handleConsent = async (childId) => {
    if (!checked[childId]) { toast.error('Please check the consent checkbox'); return; }
    setConsenting(childId);
    try {
      const r = await api.post('/parent/consent', { child_id: childId, consent_granted: true });
      toast.success(r.data.message || 'Consent recorded');
      setChildren(prev => prev.map(c => c.id === childId ? { ...c, consent_granted: true, onboarding_status: 'active' } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit consent');
    }
    setConsenting(null);
  };

  const allConsented = children.length > 0 && children.every(c => c.consent_granted);
  const pending = children.filter(c => !c.consent_granted);

  if (!user || user.role !== 'parent') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <p className="text-sm text-[#4B5563]">Please sign in as a parent to access this page.</p>
          <Link to="/login"><Button className="mt-4 rounded-xl bg-[#F97D6A] hover:bg-[#e86c5a] text-white">Sign In</Button></Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F97D6A]" />
      </div>
    );
  }

  // All children have consent — redirect or show done state
  if (allConsented) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="consent-complete-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-2">All Set!</h1>
            <p className="text-sm text-[#4B5563] mb-6">
              Privacy consent is complete for {children.length === 1 ? 'your child' : 'all your children'}. They can now participate in CareersPOV activities.
            </p>
            <Button onClick={() => navigate('/parent')} className="w-full bg-[#F97D6A] hover:bg-[#e86c5a] text-white rounded-xl h-11 font-semibold" data-testid="go-to-dashboard-btn">
              Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F8F9FA] py-8 px-4" data-testid="consent-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-6 w-6 text-[#F97D6A]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-2">One More Step to Get Started</h1>
          <p className="text-sm text-[#4B5563] max-w-md mx-auto">
            Your child's school has registered them with CareersPOV to help them explore careers and discover real-world professional opportunities.
          </p>
          <p className="text-sm text-[#4B5563] mt-2 max-w-md mx-auto">
            Before we begin personalizing their career journey, please review how their information will be used.
          </p>
        </div>

        {/* Per-child consent cards */}
        {children.map(child => (
          <div key={child.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4" data-testid={`consent-card-${child.id}`}>
            {/* Student info */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#F9A88C] flex items-center justify-center text-white font-bold">
                {child.full_name?.[0] || '?'}
              </div>
              <div>
                <p className="font-semibold text-[#1F2937]" data-testid={`consent-child-name-${child.id}`}>{child.full_name}</p>
                <p className="text-xs text-[#6B7280]">Grade {child.grade}{child.division ? ` · Division ${child.division}` : ''} · {child.school_name}</p>
              </div>
              {child.consent_granted && (
                <Badge className="border-0 bg-emerald-50 text-emerald-600 text-xs ml-auto">Consent Granted</Badge>
              )}
            </div>

            {child.consent_granted ? (
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs text-emerald-700 font-medium">Privacy consent recorded{child.consent_date ? ` on ${new Date(child.consent_date).toLocaleDateString()}` : ''}</p>
              </div>
            ) : (
              <>
                {/* Privacy Notice */}
                <div className="bg-[#F8F9FA] rounded-xl p-4 mb-4 text-sm text-[#4B5563] space-y-3">
                  <p className="font-semibold text-[#1F2937] text-xs uppercase tracking-wider">Privacy Notice</p>
                  <div className="space-y-2">
                    <div>
                      <p className="font-medium text-[#1F2937] text-xs mb-0.5">What we collect</p>
                      <p className="text-xs text-[#6B7280]">Student name, grade, division, school affiliation, career interests, and participation records in career exploration activities.</p>
                    </div>
                    <div>
                      <p className="font-medium text-[#1F2937] text-xs mb-0.5">How it is used</p>
                      <p className="text-xs text-[#6B7280]">To personalize career exploration recommendations, match students with relevant career experiences, track participation, and enable school coordinators to manage the programme.</p>
                    </div>
                    <div>
                      <p className="font-medium text-[#1F2937] text-xs mb-0.5">Who can access</p>
                      <p className="text-xs text-[#6B7280]">Your child's school coordinator, approved mentors (limited to session-relevant information), and CareersPOV platform administrators. Student data is not shared with third parties for marketing.</p>
                    </div>
                    <div>
                      <p className="font-medium text-[#1F2937] text-xs mb-0.5">Withdrawing consent</p>
                      <p className="text-xs text-[#6B7280]">You may withdraw consent at any time by contacting your school coordinator or CareersPOV. Withdrawal will prevent further participation in personalized career activities.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2 border-t border-[#E5E7EB]">
                    <Link to="/privacy" target="_blank" className="text-xs text-[#F97D6A] hover:underline flex items-center gap-1">
                      Privacy Policy <ExternalLink className="h-3 w-3" />
                    </Link>
                    <Link to="/terms" target="_blank" className="text-xs text-[#F97D6A] hover:underline flex items-center gap-1">
                      Terms of Use <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer mb-4" data-testid={`consent-checkbox-label-${child.id}`}>
                  <input
                    type="checkbox"
                    checked={!!checked[child.id]}
                    onChange={e => setChecked(prev => ({ ...prev, [child.id]: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-[#D1D5DB] text-[#F97D6A] focus:ring-[#F97D6A]"
                    data-testid={`consent-checkbox-${child.id}`}
                  />
                  <span className="text-xs text-[#4B5563] leading-relaxed">
                    I am the parent or legal guardian of <strong>{child.full_name}</strong>. I have read the Privacy Notice and consent to the processing of my child's personal information for the purposes described.
                  </span>
                </label>

                <Button
                  onClick={() => handleConsent(child.id)}
                  disabled={!checked[child.id] || consenting === child.id}
                  className="w-full rounded-xl h-10 bg-[#F97D6A] hover:bg-[#e86c5a] text-white font-semibold text-sm"
                  data-testid={`consent-submit-${child.id}`}
                >
                  {consenting === child.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Agree & Continue
                </Button>
              </>
            )}
          </div>
        ))}

        {pending.length === 0 && children.length > 0 && (
          <div className="text-center mt-6">
            <Button onClick={() => navigate('/parent')} className="bg-[#F97D6A] hover:bg-[#e86c5a] text-white rounded-xl h-11 font-semibold px-8" data-testid="go-to-dashboard-btn">
              Continue to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        <p className="text-[10px] text-[#9CA3AF] text-center mt-6">
          Privacy Policy v{PRIVACY_POLICY_VERSION} · Consent Notice v{CONSENT_NOTICE_VERSION}
        </p>
      </div>
    </div>
  );
}
